import React, { useState } from "react";
import { notify, notifyError } from "../../../utils/toast";
import { HiTicket, HiCheck } from "react-icons/hi2";
import { HiXMark } from "react-icons/hi2";
import { track } from "@amplitude/analytics-browser";

interface FirstPurchaseCouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCoupon: (type: "TODAY_1000" | "WEEKLY_100") => Promise<boolean>;
}

interface CouponOption {
  id: "TODAY_1000" | "WEEKLY_100";
  amount: number;
  description: React.ReactNode;
}

const COUPON_OPTIONS: CouponOption[] = [
  {
    id: "TODAY_1000",
    amount: 1000,
    description: (
      <>
        오늘 자정까지
        <br />
        사용 가능해요
      </>
    ),
  },
  {
    id: "WEEKLY_100",
    amount: 100,
    description: (
      <>
        7일 동안 여유롭게
        <br />쓸 수 있어요
      </>
    ),
  },
];

interface CouponCardProps {
  coupon: CouponOption;
  isSelected: boolean;
  onSelect: () => void;
}

function CouponCard({ coupon, isSelected, onSelect }: CouponCardProps) {
  const isBest = coupon.id === "TODAY_1000";

  return (
    <button
      onClick={onSelect}
      className={`w-full ${
        isBest ? "p-4 py-6" : "p-4"
      } rounded-2xl border-2 transition-all duration-200 text-left relative overflow-hidden ${
        isSelected
          ? "border-[#1562fc] bg-blue-50 shadow-lg scale-[1.02] z-10"
          : "border-gray-100 bg-white hover:border-[#1562fc]/30 hover:shadow-md z-0"
      }`}
    >
      {isBest && (
        <div className="absolute top-0 right-0 bg-[#FF5F00] text-white text-[10px] font-bold px-2 py-1 rounded-bl-xl z-20">
          🔥 인기
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div
            className={`${
              isBest ? "w-14 h-14" : "w-12 h-12"
            } rounded-xl flex items-center justify-center shrink-0 transition-all ${
              isSelected ? "bg-[#1562fc]" : "bg-blue-50"
            }`}
          >
            <HiTicket
              className={`${isBest ? "w-8 h-8" : "w-6 h-6"} transition-all ${
                isSelected ? "text-white" : "text-[#1562fc]"
              }`}
            />
          </div>
          <div className="text-left">
            <div className="flex items-baseline gap-1 mb-1">
              <span
                className={`font-bold leading-none transition-all ${
                  isBest
                    ? "text-[32px] text-[#1562fc]"
                    : "text-[20px] text-gray-900"
                }`}
              >
                {coupon.amount.toLocaleString()}
              </span>
              <span
                className={`${
                  isBest ? "text-[18px]" : "text-[16px]"
                } font-bold text-gray-900 ml-0.5 transition-all`}
              >
                원
              </span>
            </div>
            <p
              className={`text-[14px] font-medium transition-colors break-keep leading-tight ${
                isBest && isSelected
                  ? "text-[#FF5F00] font-bold"
                  : "text-gray-500"
              }`}
            >
              {coupon.description}
            </p>
          </div>
        </div>
        <div
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
            isSelected
              ? "border-[#1562fc] bg-[#1562fc]"
              : "border-gray-300 bg-white"
          }`}
        >
          {isSelected && <HiCheck className="w-4 h-4 text-white" />}
        </div>
      </div>
    </button>
  );
}

const FirstPurchaseCouponModal = ({
  isOpen,
  onClose,
  onSelectCoupon,
}: FirstPurchaseCouponModalProps) => {
  const [selectedOption, setSelectedOption] = useState<
    "TODAY_1000" | "WEEKLY_100" | null
  >("TODAY_1000");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedOption) return;

    track("첫 구매 쿠폰 받기 클릭", {
      금액: selectedOption === "TODAY_1000" ? 1000 : 100,
    });

    setIsSubmitting(true);
    const success = await onSelectCoupon(selectedOption);
    setIsSubmitting(false);

    if (success) {
      notify("쿠폰이 발급되었습니다!");
      onClose();
    } else {
      notifyError("쿠폰 발급에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black bg-opacity-50 p-4 animate-[fadeIn_0.2s_ease-out]"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden relative animate-[slideUp_0.3s_cubic-bezier(0.16,1,0.3,1)]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-500 z-30"
        >
          <HiXMark className="w-6 h-6" />
        </button>

        <div className="p-6 pt-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              원하는 혜택을 선택해주세요
            </h2>
            <p className="text-gray-500 text-sm">
              한 번 선택하면 변경할 수 없어요!
            </p>
          </div>

          <div className="flex flex-col gap-3 mb-8">
            {COUPON_OPTIONS.map((coupon) => (
              <CouponCard
                key={coupon.id}
                coupon={coupon}
                isSelected={selectedOption === coupon.id}
                onSelect={() => setSelectedOption(coupon.id)}
              />
            ))}
          </div>

          <button
            disabled={!selectedOption || isSubmitting}
            onClick={handleSubmit}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-200 transform ${
              selectedOption
                ? "bg-[#1562fc] text-white hover:bg-blue-600 shadow-lg active:scale-[0.98]"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>쿠폰 받는 중...</span>
              </div>
            ) : (
              "쿠폰 받기"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstPurchaseCouponModal;
